import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

declare global {
  interface Window {
    Cookiebot?: {
      consent: {
        marketing: boolean;
        statistics: boolean;
        preferences: boolean;
        necessary: boolean;
      };
    };
  }
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [cookiebotDetected, setCookiebotDetected] = useState(false);

  useEffect(() => {
    // Check if Cookiebot is available
    const checkCookiebot = () => {
      if (window.Cookiebot) {
        setCookiebotDetected(true);
        return true;
      }
      return false;
    };

    // Initial check
    if (checkCookiebot()) {
      return;
    }

    // Wait for Cookiebot to load, then check if we need fallback
    const checkTimer = setTimeout(() => {
      if (!checkCookiebot()) {
        // Cookiebot not available, use fallback
        const consent = localStorage.getItem("cookie-consent");
        if (!consent) {
          setIsVisible(true);
        }
      }
    }, 3000); // Wait 3 seconds for Cookiebot to load

    return () => clearTimeout(checkTimer);
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookie-consent", "accepted");
    setIsVisible(false);

    // Trigger analytics initialization if needed
    window.dispatchEvent(new CustomEvent("cookies-accepted"));
  };

  const handleDecline = () => {
    localStorage.setItem("cookie-consent", "declined");
    setIsVisible(false);

    // Trigger analytics cleanup if needed
    window.dispatchEvent(new CustomEvent("cookies-declined"));
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  // Don't show if Cookiebot is detected or not visible
  if (!isVisible || cookiebotDetected) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md">
      <Card className="p-4 border border-border bg-card shadow-lg">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-heading font-semibold text-card-foreground text-sm">
            Cookie Preferences
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-auto p-1 text-muted-foreground hover:text-card-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          We use cookies and similar technologies to improve your experience,
          analyze usage, and assist with marketing efforts. This helps us
          provide better services and understand how our NIS2 compliance
          platform is used.
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={handleAccept} size="sm" className="flex-1">
            Accept All
          </Button>
          <Button
            variant="outline"
            onClick={handleDecline}
            size="sm"
            className="flex-1"
          >
            Decline
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-2">
          You can change your preferences at any time in your profile settings.
        </p>
      </Card>
    </div>
  );
}
